import { useEffect, useMemo, useState } from "react";
import { PlatformAlerts } from "@/components/PlatformAlerts";
import { useNavigate } from "react-router-dom";
import {
  Crown, Building2, Users, TrendingUp, Sparkles, Ban, Check, X, Search,
  Mail, Calendar, ArrowLeft, ShieldCheck, MoreVertical, Zap, Star, ExternalLink, Ticket, Copy
} from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PLAN_META = {
  free:     { color: "#64748B", bg: "#F1F5F9", label: "FREEMIUM", Icon: Building2 },
  pro:      { color: "#166534", bg: "#DCFCE7", label: "PRO",      Icon: Sparkles },
};

const SUB_STATUS_META = {
  trialing:   { color: "#B45309", bg: "#FEF3C7", label_es: "En prueba",   label_en: "Trialing" },
  active:     { color: "#166534", bg: "#DCFCE7", label_es: "Activa",      label_en: "Active" },
  past_due:   { color: "#B91C1C", bg: "#FEE2E2", label_es: "Impagada",    label_en: "Past due" },
  canceled:   { color: "#64748B", bg: "#F1F5F9", label_es: "Cancelada",   label_en: "Canceled" },
  incomplete: { color: "#B91C1C", bg: "#FEE2E2", label_es: "Incompleta",  label_en: "Incomplete" },
};

const PHASE_META = {
  new:            { emoji: "🆕", color: "#2563EB", bg: "#DBEAFE", label_es: "Sin actividad",  label_en: "New" },
  trial:          { emoji: "🕐", color: "#B45309", bg: "#FEF3C7", label_es: "En prueba",       label_en: "Trial" },
  paying:         { emoji: "💰", color: "#166534", bg: "#DCFCE7", label_es: "Pagando",         label_en: "Paying" },
  grandfathered:  { emoji: "", color: "#7C3AED", bg: "#EDE9FE", label_es: "Fatherer",        label_en: "Fatherer" },
  expired:        { emoji: "🔒", color: "#B91C1C", bg: "#FEE2E2", label_es: "Prueba expirada", label_en: "Expired" },
};

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB",
      { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

export default function SuperAdminTenants() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const isES = lang === "es";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showCoupons, setShowCoupons] = useState(false);

  useEffect(() => {
    if (user && !user.is_super_admin) nav("/");
  }, [user, nav]);

  const reload = async () => {
    setLoading(true);
    try {
      const [ts, st, cp] = await Promise.all([
        api.get("/super-admin/tenants"),
        api.get("/super-admin/stats"),
        api.get("/super-admin/coupons").catch(() => ({ data: [] })),
      ]);
      setItems(ts.data);
      setStats(st.data);
      setCoupons(cp.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((t) => {
      if (planFilter !== "all" && (t.plan || "free") !== planFilter) return false;
      if (phaseFilter !== "all" && (t.phase || "new") !== phaseFilter) return false;
      if (!q) return true;
      return (t.name || "").toLowerCase().includes(q) ||
             (t.contact_email || "").toLowerCase().includes(q) ||
             (t.slug || "").toLowerCase().includes(q);
    });
  }, [items, query, planFilter, phaseFilter]);

  // Recuentos por fase para mostrar en los filtros
  const phaseCounts = useMemo(() => {
    const c = { all: items.length, new: 0, trial: 0, paying: 0, grandfathered: 0, expired: 0 };
    items.forEach((t) => { c[t.phase || "new"] = (c[t.phase || "new"] || 0) + 1; });
    return c;
  }, [items]);

  const updateTenant = async (tenantId, patch) => {
    try {
      const { data } = await api.patch(`/super-admin/tenants/${tenantId}`, patch);
      setItems((prev) => prev.map((t) => (t.id === tenantId ? data : t)));
      if (selected?.id === tenantId) setSelected(data);
      await reload();
    } catch (e) { alert(fmtErr(e)); }
  };

  const StatCard = ({ Icon, label, value, color, bg, testId }) => (
    <div className="sns-card p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: bg, color }}>
          <Icon size={20} strokeWidth={2.4} />
        </div>
        <div>
          <div className="text-2xl font-black leading-none" style={{ color }}>{value}</div>
          <div className="text-xs text-slate-500 mt-1 font-semibold">{label}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto pb-24" data-testid="page-super-tenants">
      <PlatformAlerts showSimulation />
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center lg:hidden">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="text-xs font-bold text-[var(--coral)] tracking-widest uppercase mb-1 inline-flex items-center gap-1">
            <ShieldCheck size={12} /> {isES ? "Super Admin" : "Super Admin"}
          </div>
          <h1 className="sns-title text-2xl">{isES ? "Usuarios app" : "App users"}</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {isES
              ? "Empresas que usan la app. Solo paga el gerente: sus admins y empleados funcionan con el plan del gerente."
              : "Companies using the app. Only the manager pays: their admins and employees follow the manager's plan."}
          </p>
        </div>
        <button onClick={() => setShowCoupons(true)}
                className="sns-btn bg-white border border-[var(--border)] text-slate-700 hover:bg-slate-50 px-3 py-2 text-xs inline-flex items-center gap-1.5 shrink-0"
                data-testid="btn-open-coupons">
          <Ticket size={14} /> {isES ? "Cupones" : "Coupons"} ({coupons.length})
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
          <StatCard Icon={Building2}  label={isES ? "Clientes"  : "Customers"}  value={stats.tenants_total}    color="#0F2A4D" bg="#DBEAFE" testId="stat-total" />
          <StatCard Icon={Zap}        label={isES ? "En prueba" : "Trialing"}   value={phaseCounts.trial || 0} color="#B45309" bg="#FEF3C7" testId="stat-trialing" />
          <StatCard Icon={Sparkles}   label={isES ? "PRO pagando" : "PRO paying"} value={phaseCounts.paying || 0} color="#166534" bg="#DCFCE7" testId="stat-pro" />
          <StatCard Icon={Ban} label={isES ? "Prueba caducada" : "Trial expired"} value={phaseCounts.expired || 0} color="#B91C1C" bg="#FEE2E2" testId="stat-expired" />
          <div className="sns-card p-4 col-span-2 md:col-span-4 lg:col-span-1 text-white" style={{ background: "linear-gradient(135deg,#0F2A4D,#0B2140)" }} data-testid="stat-mrr">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-1">
              MRR · {isES ? "Ingresos" : "Revenue"}
            </div>
            <div className="text-2xl font-black leading-none">
              {(stats.mrr_eur || 0).toLocaleString(isES ? "es-ES" : "en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
              <span className="text-xs text-white/60 ml-1">/{isES ? "mes" : "mo"}</span>
            </div>
            <div className="text-[10px] text-white/60 mt-1.5">
              ARR · {(stats.arr_eur || 0).toLocaleString(isES ? "es-ES" : "en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>
      )}

      <PlanLegend />

      {/* Filters */}
      <div className="sns-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2">
          {[
            { v: "all",      label: isES ? "Todos" : "All",       count: items.length },
            { v: "free",     label: "FREEMIUM",                   count: items.filter(t => t.plan === "free").length },
            { v: "pro",      label: "PRO",                        count: items.filter(t => t.plan === "pro").length },
          ].map((f) => (
            <button key={f.v}
                    onClick={() => setPlanFilter(f.v)}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold transition-all ${
                      planFilter === f.v
                        ? "bg-[var(--ink)] text-white shadow-sm"
                        : "bg-white text-slate-600 border border-[var(--border)] hover:bg-slate-50"
                    }`}
                    data-testid={`filter-plan-${f.v}`}>
              {f.label}
              <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-black ${
                planFilter === f.v ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
              }`}>{f.count}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text"
                 placeholder={isES ? "Buscar por nombre, email, slug..." : "Search by name, email, slug..."}
                 value={query}
                 onChange={(e) => setQuery(e.target.value)}
                 className="sns-input pl-9 py-2 text-sm"
                 data-testid="tenants-search" />
        </div>
      </div>

      {/* Filtro por FASE del ciclo de vida */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="phase-filters">
        {[
          { v: "all",           label: isES ? "Todas las fases" : "All phases", emoji: "📊" },
          { v: "new",           label: PHASE_META.new[isES ? "label_es" : "label_en"],           emoji: PHASE_META.new.emoji },
          { v: "trial",         label: PHASE_META.trial[isES ? "label_es" : "label_en"],         emoji: PHASE_META.trial.emoji },
          { v: "paying",        label: PHASE_META.paying[isES ? "label_es" : "label_en"],        emoji: PHASE_META.paying.emoji },
          { v: "grandfathered", label: PHASE_META.grandfathered[isES ? "label_es" : "label_en"], emoji: PHASE_META.grandfathered.emoji },
          { v: "expired",       label: PHASE_META.expired[isES ? "label_es" : "label_en"],       emoji: PHASE_META.expired.emoji },
        ].map((f) => (
          <button key={f.v}
                  onClick={() => setPhaseFilter(f.v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    phaseFilter === f.v
                      ? "bg-[var(--coral)] text-white shadow-sm"
                      : "bg-white text-slate-600 border border-[var(--border)] hover:bg-slate-50"
                  }`}
                  data-testid={`filter-phase-${f.v}`}>
            {f.emoji && <span>{f.emoji}</span>}
            <span>{f.label}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
              phaseFilter === f.v ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
            }`}>{phaseCounts[f.v] || 0}</span>
          </button>
        ))}
      </div>

      {/* Tenant list */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">{isES ? "Cargando..." : "Loading..."}</div>
      ) : filtered.length === 0 ? (
        <div className="sns-card p-10 text-center" data-testid="tenants-empty">
          <Building2 size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-semibold">{isES ? "Sin clientes que coincidan." : "No customers match."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((tenant) => {
            const planMeta = PLAN_META[tenant.plan || "free"];
            const phaseMeta = PHASE_META[tenant.phase] || PHASE_META.new;
            const PlanIcon = planMeta.Icon;
            return (
              <div key={tenant.id} className="sns-card p-4 md:p-5" data-testid={`tenant-card-${tenant.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                       style={{ background: planMeta.bg, color: planMeta.color }}>
                    <PlanIcon size={20} strokeWidth={2.4} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-800 truncate">{tenant.name}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider inline-flex items-center gap-1"
                            style={{ background: phaseMeta.bg, color: phaseMeta.color }}
                            data-testid={`tenant-phase-${tenant.id}`}>
                        {phaseMeta.emoji && <span>{phaseMeta.emoji}</span>}
                        {isES ? phaseMeta.label_es.toUpperCase() : phaseMeta.label_en.toUpperCase()}
                        {tenant.phase === "trial" && tenant.trial_days_left != null && (
                          <span className="ml-0.5">· {tenant.trial_days_left}d</span>
                        )}
                        {tenant.phase === "paying" && tenant.billing_cycle && (
                          <span className="ml-0.5">· {tenant.billing_cycle === "annual" ? (isES ? "ANUAL" : "YEARLY") : (isES ? "MENSUAL" : "MONTHLY")}</span>
                        )}
                      </span>
                      {!tenant.active && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                          {isES ? "DESACTIVADO" : "DISABLED"}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      {tenant.owner_email && tenant.owner_email !== tenant.contact_email && (
                        <span className="inline-flex items-center gap-1 font-semibold text-slate-600"><Crown size={12} /> {tenant.owner_email}</span>
                      )}
                      {tenant.contact_email && (
                        <a href={`mailto:${tenant.contact_email}`} className="inline-flex items-center gap-1 hover:text-[var(--coral)]">
                          <Mail size={12} /> {tenant.contact_email}
                        </a>
                      )}
                      <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(tenant.created_at, lang)}</span>
                      {tenant.last_owner_login && (
                        <span className="inline-flex items-center gap-1 text-slate-400">
                          · {isES ? "Última conexión:" : "Last login:"} {fmtDate(tenant.last_owner_login, lang)}
                        </span>
                      )}
                    </div>
                    {/* Employee usage bar */}
                    {(tenant.users_count != null) && (
                      <div className="mt-2 flex items-center gap-2" data-testid={`tenant-employees-${tenant.id}`}>
                        <Users size={12} className="text-slate-400 shrink-0" />
                        <span className="text-xs font-bold text-slate-700">
                          {tenant.users_count}
                          {tenant.employees_limit != null ? ` / ${tenant.employees_limit}` : " / ∞"}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {isES ? "empleados" : "employees"}
                        </span>
                        {tenant.employees_limit != null && (
                          <div className="flex-1 max-w-[160px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(100, (tenant.users_count / tenant.employees_limit) * 100)}%`,
                                background: tenant.users_count >= tenant.employees_limit ? "#ef4444"
                                  : tenant.users_count / tenant.employees_limit >= 0.8 ? "#f59e0b"
                                  : "#10b981"
                              }}
                            />
                          </div>
                        )}
                        {tenant.employees_limit != null && tenant.users_count >= tenant.employees_limit && (
                          <span className="text-[10px] font-bold text-red-600">
                            {isES ? "AL LÍMITE" : "AT LIMIT"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {tenant.phase === "expired" && <ExtensionButton tenant={tenant} isES={isES} onDone={reload} />}
                  <button onClick={() => setSelected(tenant)}
                          className="sns-btn sns-btn-ghost bg-slate-100 px-3 py-2 text-xs shrink-0"
                          data-testid={`tenant-open-${tenant.id}`}>
                    {isES ? "Gestionar" : "Manage"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <TenantDrawer tenant={selected} isES={isES}
                      onClose={() => setSelected(null)}
                      onChangePlan={(plan) => updateTenant(selected.id, { plan })}
                      onToggleActive={() => updateTenant(selected.id, { active: !selected.active })}
                      onToggleGrandfathered={() => updateTenant(selected.id, { grandfathered: !selected.grandfathered })}
                      onDeleted={() => { setSelected(null); reload(); }} />
      )}

      {showCoupons && (
        <CouponsModal coupons={coupons} isES={isES} onClose={() => setShowCoupons(false)} />
      )}
    </div>
  );
}

/* ============ Coupons Modal ============ */
function CouponsModal({ coupons, isES, onClose }) {
  const copy = (text) => navigator.clipboard.writeText(text);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="coupons-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-br from-[#0F2A4D] to-[#060E1E] text-white p-6 flex items-start justify-between">
          <div>
            <div className="text-xs text-cyan-300 uppercase tracking-widest font-bold mb-1">
              {isES ? "Descuentos disponibles" : "Available discounts"}
            </div>
            <h2 className="text-xl font-bold inline-flex items-center gap-2">
              <Ticket size={20} /> {isES ? "Cupones" : "Coupons"}
            </h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-slate-500 mb-4">
            {isES
              ? "Comparte estos códigos con clientes potenciales. Los aplicarán al pagar en el checkout de Stripe."
              : "Share these codes with prospects. They'll apply at Stripe checkout."}
          </p>
          <div className="space-y-3">
            {coupons.map((c) => (
              <div key={c.id} className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50" data-testid={`coupon-${c.code}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border-2 border-dashed border-[var(--coral)] font-mono text-sm font-black text-[var(--coral)]">
                    {c.code}
                  </div>
                  <button onClick={() => copy(c.code)}
                          className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-[var(--coral)]"
                          title="Copiar">
                    <Copy size={16} />
                  </button>
                </div>
                <div className="text-sm font-bold text-slate-800">{c.name}</div>
                <div className="text-xs text-slate-500 mt-1">
                  <b>{c.percent_off}%</b> {isES ? "de descuento" : "off"} · {isES ? "Aplica una vez" : "Applies once"}
                </div>
              </div>
            ))}
            {coupons.length === 0 && (
              <p className="text-center text-slate-400 py-6">
                {isES ? "Aún no hay cupones configurados." : "No coupons configured yet."}
              </p>
            )}
          </div>
          <div className="mt-5 p-3 rounded-xl bg-blue-50 text-xs text-blue-700 border border-blue-100">
            💡 {isES
              ? "También puedes crear cupones personalizados directamente en el dashboard de Stripe → Products → Coupons."
              : "You can also create custom coupons directly in your Stripe dashboard → Products → Coupons."}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ Tenant Drawer ============ */
function TenantDrawer({ tenant, isES, onClose, onChangePlan, onToggleActive, onToggleGrandfathered, onDeleted }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/super-admin/tenants/${tenant.id}`).then(r => setDetail(r.data)).catch(console.error);
  }, [tenant.id]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="tenant-drawer">
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-br from-[#0F2A4D] to-[#060E1E] text-white p-6 flex items-start justify-between">
          <div>
            <div className="text-xs text-cyan-300 uppercase tracking-widest font-bold mb-1">Workspace</div>
            <h2 className="text-xl font-bold">{tenant.name}</h2>
            <div className="text-xs text-white/60 mt-1">{tenant.slug}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="drawer-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Plan selector */}
          <div>
            <div className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-2">
              {isES ? "Plan actual" : "Current plan"}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {["free", "pro"].map((p) => (
                <button key={p} onClick={() => onChangePlan(p)}
                        className={`py-3 rounded-xl text-xs font-black tracking-wider border-2 transition-all ${
                          tenant.plan === p ? "border-[var(--coral)] bg-[var(--coral)]/5 text-[var(--coral)]" : "border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}
                        data-testid={`drawer-plan-${p}`}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={onToggleGrandfathered}
                    disabled={!tenant.house && !tenant.grandfathered}
                    className="text-left p-4 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    data-testid="drawer-toggle-grandfathered">
              <div className="flex items-center gap-2 mb-1">
                <Star size={14} className="text-amber-500" />
                <div className="text-sm font-bold">Fatherer</div>
                <div className={`ml-auto text-[10px] font-black px-2 py-0.5 rounded-full ${
                  tenant.grandfathered ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                }`}>
                  {tenant.grandfathered ? "ON" : "OFF"}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                {isES ? "Pro gratis de por vida. Solo cuentas @smileandshine.es" : "Free Pro forever. Only @smileandshine.es accounts"}
              </div>
            </button>

            <button onClick={onToggleActive}
                    className="text-left p-4 rounded-xl border border-slate-200 hover:border-red-300 hover:bg-red-50/30"
                    data-testid="drawer-toggle-active">
              <div className="flex items-center gap-2 mb-1">
                <Ban size={14} className="text-red-500" />
                <div className="text-sm font-bold">{isES ? "Activo" : "Active"}</div>
                <div className={`ml-auto text-[10px] font-black px-2 py-0.5 rounded-full ${
                  tenant.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                }`}>
                  {tenant.active !== false ? "ON" : "OFF"}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                {isES ? "Si está OFF, sus usuarios no pueden entrar" : "If OFF, users cannot log in"}
              </div>
            </button>
          </div>

          {!tenant.house && <DeleteTenantZone tenant={tenant} onDeleted={onDeleted} />}

          {/* Stripe info */}
          {tenant.stripe_customer_id && (
            <div className="p-4 rounded-xl bg-slate-50">
              <div className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-2">Stripe</div>
              <div className="space-y-1 text-xs font-mono text-slate-600">
                <div>customer: {tenant.stripe_customer_id}</div>
                {tenant.stripe_subscription_id && <div>subscription: {tenant.stripe_subscription_id}</div>}
                {tenant.stripe_subscription_status && <div>status: <b>{tenant.stripe_subscription_status}</b></div>}
                {tenant.subscription_current_period_end && (
                  <div>{isES ? "Próxima renovación" : "Next renewal"}: {new Date(tenant.subscription_current_period_end).toLocaleString()}</div>
                )}
              </div>
            </div>
          )}

          {/* Upcoming invoice */}
          {detail?.upcoming_invoice && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="text-xs uppercase font-bold text-amber-800 tracking-widest mb-2">
                {isES ? "Próxima factura" : "Upcoming invoice"}
              </div>
              <div className="flex items-baseline justify-between">
                <div className="text-2xl font-black text-amber-900">
                  {((detail.upcoming_invoice.amount_due || 0) / 100).toLocaleString(isES ? "es-ES" : "en-GB", { style: "currency", currency: (detail.upcoming_invoice.currency || "eur").toUpperCase() })}
                </div>
                {detail.upcoming_invoice.next_payment_attempt && (
                  <div className="text-xs text-amber-700">
                    {isES ? "El" : "On"} {new Date(detail.upcoming_invoice.next_payment_attempt * 1000).toLocaleDateString(isES ? "es-ES" : "en-GB")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Historic invoices */}
          {detail?.invoices?.length > 0 && (
            <div>
              <div className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-2">
                {isES ? "Últimas facturas" : "Recent invoices"} ({detail.invoices.length})
              </div>
              <div className="space-y-2">
                {detail.invoices.map((inv) => {
                  const paid = inv.status === "paid";
                  return (
                    <div key={inv.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 text-sm">
                      <div className={`w-2 h-2 rounded-full ${paid ? "bg-emerald-500" : inv.status === "open" ? "bg-amber-500" : "bg-slate-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 text-xs">{inv.number || inv.id}</div>
                        <div className="text-[11px] text-slate-500">
                          {new Date(inv.created * 1000).toLocaleDateString(isES ? "es-ES" : "en-GB")} · <span className="font-bold uppercase">{inv.status}</span>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-slate-800">
                        {((inv.amount_paid || inv.amount_due) / 100).toLocaleString(isES ? "es-ES" : "en-GB", { style: "currency", currency: (inv.currency || "eur").toUpperCase(), maximumFractionDigits: 2 })}
                      </div>
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                           className="p-1.5 rounded-lg text-slate-400 hover:text-[var(--coral)] hover:bg-white"
                           title={isES ? "Ver factura" : "View invoice"}>
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Usage & users */}
          {detail && (
            <>
              <div>
                <div className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-2">
                  {isES ? "Uso actual" : "Current usage"}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: isES ? "Clientes" : "Clients",     value: detail.stats.clientes },
                    { label: isES ? "Propiedades" : "Properties", value: detail.stats.embarcaciones },
                    { label: isES ? "Servicios" : "Services",   value: detail.stats.servicios },
                    { label: isES ? "Trabajos" : "Jobs",        value: detail.stats.mantenimientos },
                  ].map((s) => (
                    <div key={s.label} className="p-3 rounded-xl bg-slate-50 text-center">
                      <div className="text-lg font-black text-slate-800">{s.value}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase font-bold text-slate-400 tracking-widest mb-2">
                  {isES ? "Usuarios" : "Users"} ({detail.users.length})
                </div>
                <div className="space-y-2">
                  {detail.users.map((u) => (
                    <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 text-sm">
                      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-slate-500 font-bold text-xs">
                        {(u.name || "?").substring(0,2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{u.name}</div>
                        <div className="text-xs text-slate-500 truncate">{u.email}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                        u?.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {(u?.role || "?").toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ Leyenda de planes ============ */
function PlanLegend() {
  const rows = [
    { tag: "FREEMIUM", color: "#64748B", bg: "#F1F5F9", text: "Gratis con límites: 2 propiedades, 1 empleado y 10 mantenimientos al mes. Sin Holded ni exportaciones." },
    { tag: "PRO", color: "#166534", bg: "#DCFCE7", text: "De pago: 45,99 €/mes o 441,50 €/año (20 % dto.), + IVA. Hasta 50 propiedades/embarcaciones, 50 usuarios y 30 empleados activos a la vez; trabajos sin límite. Las cuentas nuevas lo prueban 14 días gratis." },
    { tag: "FATHERER", color: "#7C3AED", bg: "#EDE9FE", text: "Pro gratis de por vida, solo para la empresa de @smileandshine.es y sus empleados." },
  ];
  return (
    <div className="sns-card p-4 mb-4 grid md:grid-cols-3 gap-3" data-testid="plan-legend">
      {rows.map((r) => (
        <div key={r.tag} className="flex gap-3 items-start">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider shrink-0" style={{ background: r.bg, color: r.color }}>{r.tag}</span>
          <span className="text-xs text-slate-600 leading-relaxed">{r.text}</span>
        </div>
      ))}
    </div>
  );
}

/* ============ Ofrecer 14 días más ============ */
function ExtensionButton({ tenant, isES, onDone }) {
  const [busy, setBusy] = useState(false);
  const offer = tenant.extension_offer;
  const pending = offer?.status === "pending" && offer.expires_at > new Date().toISOString();
  const send = async () => {
    if (!window.confirm(isES ? `¿Enviar a ${tenant.owner_email} la oferta de 14 días más? Se activará cuando la confirme.` : `Send a 14-day extension offer to ${tenant.owner_email}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/super-admin/tenants/${tenant.id}/offer-extension`);
      toast.success(data.email_sent ? `Oferta enviada a ${data.email_to}` : "Oferta creada, pero el email no se pudo enviar");
      onDone();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      <button onClick={send} disabled={busy}
              className="sns-btn px-3 py-2 text-xs bg-[var(--coral)] text-white hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid={`btn-offer-extension-${tenant.id}`}>
        <Calendar size={13} /> {busy ? "Enviando…" : pending ? "Reenviar oferta 14 días" : "Ofrecer 14 días más"}
      </button>
      {pending && <span className="text-[10px] font-bold text-amber-700" data-testid={`offer-pending-${tenant.id}`}>Esperando confirmación del gerente</span>}
    </div>
  );
}

/* ============ Eliminar empresa ============ */
function DeleteTenantZone({ tenant, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/super-admin/tenants/${tenant.id}`, { params: { confirm: name } });
      toast.success(`${tenant.name} y todos sus datos se han eliminado`);
      onDeleted();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl border-2 border-red-200 bg-red-50/50" data-testid="delete-tenant-zone">
      <div className="text-sm font-bold text-red-700">Eliminar cuenta</div>
      <div className="text-xs text-red-700/80 mt-1">Borra la empresa, su gerente, empleados, propiedades, clientes, servicios y trabajos. No se puede deshacer.</div>
      {!open ? (
        <button onClick={() => setOpen(true)} className="mt-3 sns-btn px-3 py-2 text-xs bg-red-600 text-white hover:bg-red-700" data-testid="btn-open-delete-tenant">Eliminar cuenta y sus datos</button>
      ) : (
        <div className="mt-3 space-y-2">
          <label className="text-xs text-slate-700">Escribe <b>{tenant.name}</b> para confirmar:</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="sns-input !bg-white text-sm" data-testid="input-confirm-delete-tenant" />
          <div className="flex gap-2">
            <button onClick={() => { setOpen(false); setName(""); }} className="sns-btn sns-btn-ghost bg-white px-3 py-2 text-xs flex-1">Cancelar</button>
            <button onClick={del} disabled={busy || name.trim() !== (tenant.name || "").trim()}
                    className="sns-btn px-3 py-2 text-xs bg-red-600 text-white flex-1 disabled:opacity-50" data-testid="btn-confirm-delete-tenant">
              {busy ? "Eliminando…" : "Eliminar definitivamente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
